/**
 * 产品卖点展示组件
 * 实现从产品中心向外延伸的指示线和文字动画
 */
class ProductFeaturesComponent extends HTMLElement {
  constructor() {
    super();
    this.scrollTrigger = null;
    this.masterTimeline = null;
    this.resizeObserver = null;
    this.features = [];
    this.animationTimeout = null;
  }

  connectedCallback() {

    // 等待GSAP和ScrollTrigger加载完成
    if (typeof window.gsap === 'undefined' || typeof window.ScrollTrigger === 'undefined') {
      let attempts = 0;
      const maxAttempts = 100;
      const checkGSAP = setInterval(() => {
        attempts++;
        if ((typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined') || attempts >= maxAttempts) {
          clearInterval(checkGSAP);
          if (typeof window.gsap !== 'undefined' && typeof window.ScrollTrigger !== 'undefined') {
            this.init();
          } else {
            console.warn('GSAP or ScrollTrigger failed to load');
          }
        }
      }, 100);
      return;
    }

    // 确保DOM完全加载后再初始化
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      requestAnimationFrame(() => {
        setTimeout(() => this.init(), 100);
      });
    }
  }

  disconnectedCallback() {
    // 清理 ScrollTrigger 实例
    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
    }

    // 清理 GSAP 动画
    if (this.masterTimeline) {
      this.masterTimeline.kill();
    }

    // 清理 ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  init() {
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;

    console.log('Product Features: Initializing...');

    // 注册插件
    gsap.registerPlugin(ScrollTrigger);

    // 获取配置
    this.animationDelay = parseInt(this.dataset.animationDelay || '1000', 10);
    
    // 获取所有功能特性元素
    this.features = Array.from(this.querySelectorAll('.product-feature'));
    console.log('Product Features: Found', this.features.length, 'features');
    
    if (this.features.length === 0) {
      console.warn('Product Features: No features found');
      return;
    }

    // 打印每个 feature 的信息
    this.features.forEach((feature) => {
      const computedStyle = window.getComputedStyle(feature);
      console.log('Product Features: Feature data:', {
        id: feature.dataset.featureId,
        order: feature.dataset.animationOrder,
        alignment: feature.dataset.textAlignment,
        lineDirection: feature.dataset.lineDirection,
        styleAttr: feature.getAttribute('style'),
        computedTop: computedStyle.top,
        computedLeft: computedStyle.left,
      });
    });

    // 按 animation_order 排序
    this.features.sort((a, b) => {
      const orderA = parseInt(a.dataset.animationOrder || '99', 10);
      const orderB = parseInt(b.dataset.animationOrder || '99', 10);
      return orderA - orderB;
    });

    // 获取产品中心点
    this.centerPoint = this.querySelector('.product-features__center-point');
    this.section = this.querySelector('.product-features');

    // 创建主时间轴
    this.createMasterTimeline();

    console.log('Product Features: Timeline created, animation delay:', this.animationDelay);

    // 创建 ScrollTrigger
    this.scrollTrigger = ScrollTrigger.create({
      trigger: this.section,
      start: 'top center',
      end: 'bottom center',
      // markers: true, // 调试标记（可选）
      onEnter: () => {
        console.log('Product Features: Entered viewport, starting animation in', this.animationDelay, 'ms');
        // 清除之前的延迟定时器（如果有）
        if (this.animationTimeout) {
          clearTimeout(this.animationTimeout);
        }
        // 重置时间轴到开始位置（暂停状态）
        this.masterTimeline.pause(0);
        // 延迟后开始播放动画
        this.animationTimeout = setTimeout(() => {
          console.log('Product Features: Playing animation');
          this.masterTimeline.play();
        }, this.animationDelay);
      },
      onEnterBack: () => {
        console.log('Product Features: Entered viewport again (scrolling up), starting animation in', this.animationDelay, 'ms');
        // 清除之前的延迟定时器（如果有）
        if (this.animationTimeout) {
          clearTimeout(this.animationTimeout);
        }
        // 重置时间轴到开始位置（暂停状态）
        this.masterTimeline.pause(0);
        // 延迟后开始播放动画
        this.animationTimeout = setTimeout(() => {
          console.log('Product Features: Playing animation');
          this.masterTimeline.play();
        }, this.animationDelay);
      },
      onLeave: () => {
        console.log('Product Features: Left viewport (scrolling down)');
        // 清除延迟定时器（如果动画还没开始）
        if (this.animationTimeout) {
          clearTimeout(this.animationTimeout);
        }
        // 暂停动画
        this.masterTimeline.pause();
      },
      onLeaveBack: () => {
        console.log('Product Features: Left viewport (scrolling up)');
        // 清除延迟定时器（如果动画还没开始）
        if (this.animationTimeout) {
          clearTimeout(this.animationTimeout);
        }
        // 暂停动画
        this.masterTimeline.pause();
      },
    });

    // 监听窗口大小改变
    this.resizeObserver = new ResizeObserver(() => {
      ScrollTrigger.refresh();
    });
    this.resizeObserver.observe(this);
  }

  createMasterTimeline() {
    const gsap = window.gsap;
    
    // 创建主时间轴（暂停状态）
    this.masterTimeline = gsap.timeline({ paused: true });

    // 为每个功能特性创建动画
    this.features.forEach((feature, index) => {
      this.createFeatureAnimation(feature, index);
    });
  }

  createFeatureAnimation(feature, index) {
    const gsap = window.gsap;
    
    // 根据屏幕尺寸选择对应的布局方向
    const isMobile = window.innerWidth < 750;
    const side = isMobile 
      ? (feature.dataset.mobileSide || 'left')
      : (feature.dataset.desktopSide || 'left');
    
    const lineDirection = feature.dataset.lineDirection || 'horizontal';
    const content = feature.querySelector('.product-feature__content');
    const lines = feature.querySelectorAll('.product-feature__line');
    
    console.log(`Creating animation for feature ${index}, isMobile: ${isMobile}, side: ${side}, direction: ${lineDirection}`);

    // 为每个特性创建独立的时间轴
    const featureTl = gsap.timeline({
      onStart: () => {
        console.log(`Feature ${index} animation started`);
      }
    });

    // 1. 整个功能特性先显示（但透明）
    featureTl.set(feature, { opacity: 1 }, 0);

    // 2. 指示线动画 - 从0延伸
    const horizontalLine = feature.querySelector('.product-feature__line--horizontal');
    const verticalLine = feature.querySelector('.product-feature__line--vertical');

    // 读取线条宽度配置
    const computedStyle = window.getComputedStyle(feature);
    
    // 获取配置的线条宽度（单位：vw）
    const horizontalLengthVw = isMobile 
      ? parseFloat(computedStyle.getPropertyValue('--mobile-line-width') || '20vw')
      : parseFloat(computedStyle.getPropertyValue('--desktop-line-width') || '6vw');
    
    // 转换vw为像素值用于动画
    const viewportWidth = window.innerWidth;
    const horizontalLength = (horizontalLengthVw / 100) * viewportWidth;
    
    const verticalLength = 60; // 竖线长度固定
    
    console.log(`Feature ${index} line width: ${horizontalLengthVw}vw (${horizontalLength}px)`);
    
    // 横线动画 - 根据布局方向确定起点
    if (horizontalLine) {
      // 左侧布局（文案->指示器->占位）：圆点在左侧，从左向右延伸
      // 右侧布局（占位->指示器->文案）：圆点在右侧，从右向左延伸
      const origin = side === 'left' ? 'left center' : 'right center';
      gsap.set(horizontalLine, { transformOrigin: origin });
      featureTl.fromTo(
        horizontalLine,
        { scaleX: 0, opacity: 0 },
        {
          scaleX: 1,
          width: horizontalLength,
          opacity: 1,
          duration: 0.5,
          ease: 'power2.out',
        },
        0
      );
    }
    
    // 竖线动画 - 仅在 L 形模式下显示
    if (verticalLine && lineDirection === 'l-shape') {
      gsap.set(verticalLine, { transformOrigin: 'top center' });
      featureTl.fromTo(
        verticalLine,
        { scaleY: 0, opacity: 0 },
        {
          scaleY: 1,
          height: verticalLength,
          opacity: 1,
          duration: 0.5,
          ease: 'power2.out',
        },
        0.3
      );
    }

    // 3. 文案淡入动画
    featureTl.fromTo(
      content,
      {
        opacity: 0,
        y: 10,
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out',
      },
      0.4 // 线条动画进行到一半时开始
    );

    // 将特性动画添加到主时间轴
    // 根据 index 设置延迟，实现顺序出现的效果
    this.masterTimeline.add(featureTl, index * 0.5); // 每个特性间隔0.5秒
  }
}

if (!customElements.get('product-features-component')) {
  customElements.define('product-features-component', ProductFeaturesComponent);
}

